import PreCSS from "../../src/utils/preRenderCSS";
import axios from "axios";
import i18next from "../../i18n";
import Head from "next/head";

import Interweave from "interweave";
import {
  convertDate,
  ptToVwConverter,
  parseInfo,
  mobileVwConverter,
} from "../../src/utils";
import useWindowSize from "../../src/utils/useWindowSize";

import { Button } from "@material-ui/core";
import {
  TopContentsContainer,
  TopContentsTitleContainer,
  TopContentsTitle,
  DecorationLine,
} from "../../src/components/style";
import { NewsContainer } from "../../src/components/style/news";
import {
  RightContainer,
  Title,
  SubTitle,
  CategoryTitle,
  VideoWrapper,
  FlyerButton,
  InfoWrapper,
  LogoImage,
  BodyWrapper,
} from "../../src/components/style/performances";

import ShareButtons from "../../src/components/ShareButtons";
import BackToList from "../../src/components/BackToList";
import GridPics from "../../src/components/PerformanceGridPics";

import { workshopCategories } from "../../src/config/Workshop";
import Flyer from "../../src/components/Flyer";

const Post = ({
  article,
  next,
  previous,
  lang,
  id,
}: {
  article: Workshop;
  next: string;
  previous: string;
  lang: string;
  id: string;
}) => {
  const windowSize = useWindowSize();

  if (!lang) {
    setTimeout(() => {
      if (!lang) {
        if (!i18next.i18n.languages.length) {
          i18next.i18n.languages = ["ja", "en"];
        }
        i18next.i18n.changeLanguage(
          i18next.i18n.language || i18next.i18n.languages[0] || "en"
        );
      }
    }, 1000);
  }

  const translateCategory = (ja: Workshop["category"]) => {
    return workshopCategories.reduce((acc, cur) => {
      if (cur.title.ja === ja) return cur.title.en;
      return acc;
    }, "");
  };

  const subImages = [];
  for (let i = 0; i < 8; i++) {
    const index = `img${i + 1}` as keyof Workshop;
    const image = article[index] as { url: string };
    if (image) {
      subImages.push(image);
    }
  }

  const urls = [];
  const titles = [];
  if (article.flyer1) {
    urls.push(article.flyer1.url);
    titles.push(`${article.title}01`);
  }
  if (article.flyer2) {
    urls.push(article.flyer2.url);
    titles.push(`${article.title}02`);
  }

  return (
    <div>
      <PreCSS />
      <Head>
        <title>{article.title}</title>
        <meta property="og:title" content={article.title} />
        <meta property="og:description" content={article.title} />
        <meta property="og:image" content={article.imgMain?.url} />
        <meta
          property="og:url"
          content={`https://zero-dance.com/workshops/${id}`}
        />
        <meta name="twitter:card" content="summary_large_image" />
      </Head>
      <NewsContainer>
        <TopContentsContainer>
          {windowSize === "desktop" ? (
            <>
              <TopContentsTitleContainer>
                <TopContentsTitle>workshops</TopContentsTitle>
              </TopContentsTitleContainer>
              <DecorationLine />
            </>
          ) : windowSize === "mobile" || windowSize === "tablet" ? (
            <>
              <div
                style={{
                  fontSize: "18px",
                  letterSpacing: "0.1em",
                  textTransform: "uppercase",
                  fontWeight: 400,
                }}
              >
                workshops
              </div>
              <DecorationLine />
            </>
          ) : (
            <p></p>
          )}

          <RightContainer>
            <CategoryTitle
              style={{ marginBottom: `${mobileVwConverter(50)}vw` }}
            >
              <div>
                {lang === "ja"
                  ? article.category
                  : lang === "en"
                  ? translateCategory(article.category)
                  : ""}
              </div>
            </CategoryTitle>
            <Title>
              {lang === "ja"
                ? article.title
                : lang === "en"
                ? article.titleEng
                : ""}
            </Title>
            <SubTitle>
              {lang === "ja"
                ? article.location
                : lang === "en"
                ? article.locationEng
                : ""}
            </SubTitle>
            <SubTitle>{article.eventDate}</SubTitle>

            {article.video && (
              <VideoWrapper>
                <iframe
                  className="video"
                  // src={`${article.video}`}
                  src={`https://player.vimeo.com/video/${article.video}`}
                  width="100%"
                  height="100%"
                  frameBorder="0"
                  title="video of this workshop"
                ></iframe>
              </VideoWrapper>
            )}

            <BodyWrapper>
              <Interweave
                content={
                  lang === "ja"
                    ? article.body
                    : lang === "en"
                    ? article.bodyEng
                    : ""
                }
              />
            </BodyWrapper>

            <GridPics subImages={subImages} />

            <InfoWrapper>
              <Interweave
                content={parseInfo(
                  lang === "ja"
                    ? article.info
                    : lang === "en"
                    ? article.infoEng
                    : ""
                )}
              />
            </InfoWrapper>

            <div
              style={{
                display: "flex",
                marginBottom: `${ptToVwConverter(70)}vw`,
              }}
            >
              {article.logo1 ? (
                <LogoImage
                  src={article.logo1.url}
                  alt="logo1"
                  onContextMenu={(e) => {
                    e.preventDefault();
                    return false;
                  }}
                />
              ) : (
                <></>
              )}
              {article.logo2 ? (
                <LogoImage
                  src={article.logo2.url}
                  alt="logo2"
                  onContextMenu={(e) => {
                    e.preventDefault();
                    return false;
                  }}
                />
              ) : (
                <></>
              )}
            </div>

            {urls.length ? (
              <Flyer urls={urls} titles={titles} lang={lang} />
            ) : (
              <></>
            )}

            <ShareButtons />

            <BackToList main="workshops" previous={previous} next={next} />
          </RightContainer>
        </TopContentsContainer>
      </NewsContainer>
    </div>
  );
};

export async function getStaticPaths() {
  const baseURL = process.env.MICRO_URL;

  const newsURL = baseURL + "workshops?limit=1000&fields=id";

  const config = {
    headers: {
      "X-API-KEY": process.env.MICRO_API_KEY,
    },
  };

  const res: WorkshopResponse = await axios.get(newsURL, config);

  const paths = res.data.contents.map((ws) => `/workshops/${ws.id}`);

  return {
    paths,
    fallback: false,
  };
}

export async function getStaticProps(context: { params: { id: string } }) {
  const baseURL = process.env.MICRO_URL;

  const wsURL = baseURL + "workshops?limit=1000";

  const config = {
    headers: {
      "X-API-KEY": process.env.MICRO_API_KEY,
    },
  };

  const { id } = context.params;

  const all: WorkshopResponse = await axios.get(`${wsURL}`, config);

  const tempArticles = all.data.contents.sort((a, b) => {
    const recent = new Date(a.publishedAt!).getTime();
    const old = new Date(b.publishedAt!).getTime();
    return old - recent;
  });

  const currentCategory = tempArticles.reduce((acc, cur) => {
    if (cur.id === id) {
      return cur.category;
    } else {
      return acc;
    }
  }, "");

  const articles = tempArticles.filter(
    (each) => each.category === currentCategory
  );

  // tslint:disable-next-line: one-variable-per-declaration
  let article, next, previous;
  articles.forEach((each, i) => {
    if (each.id === id) {
      article = each;
      if (articles[i - 1]) {
        previous = articles[i - 1].id;
      } else {
        previous = "";
      }
      if (articles[i + 1]) {
        next = articles[i + 1].id;
      } else {
        next = "";
      }
    }
  });

  return {
    props: {
      article,
      next,
      previous,
      id,
      namespacesRequired: [],
    },
  };
}

export default Post;
